import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

// Reusable Document Print Template - Can be extended for other document types
const CargoArrivalNoticePrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch company details if not provided
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (companyData) {
        setCompany(companyData);
        setLoading(false);
        return;
      }

      try {
        // Get docket to find company_id
        const docketResponse = await fetch(`${backendUrl}/api/dockets/${docketId}`, {
          headers: getAuthHeaders()
        });
        
        if (docketResponse.ok) {
          const docketData = await docketResponse.json();
          const companyId = docketData.company_id;
          
          // Fetch company details
          const companyResponse = await fetch(`${backendUrl}/api/companies/${companyId}`, {
            headers: getAuthHeaders()
          });
          
          if (companyResponse.ok) {
            const companyDetails = await companyResponse.json();
            setCompany(companyDetails);
          }
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [docketId, companyData, backendUrl]);

  // Expose print function to parent
  useImperativeHandle(ref, () => ({
    print: () => {
      window.print();
    }
  }));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const companyName = company?.name || 'COMPANY NAME';
  const companyLogo = company?.logo_url;
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyWebsite = company?.website || '';
  const companyGst = company?.gst_number || '';
  const companyCin = company?.cin_number || '';

  return (
    <>
      {/* Print Content — id is applied by CANPrint parent, matching DocketPrint pattern */}
      <div 
        className="bg-white print:bg-white"
        style={{ 
          fontFamily: 'Arial, sans-serif',
          fontSize: '11px',
          lineHeight: '1.3'
        }}
      >
        <div className="p-6 print:p-6 relative">
          
          {/* Watermark */}
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{ zIndex: 0 }}
          >
            <div 
              className="text-gray-100 font-bold text-center whitespace-nowrap transform -rotate-45"
              style={{ 
                fontSize: '60px',
                opacity: 0.08,
                letterSpacing: '10px'
              }}
            >
              {companyName}
            </div>
          </div>

          {/* Content with higher z-index */}
          <div className="relative" style={{ zIndex: 1 }}>
            
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              {/* Left - Logo */}
              <div className="flex-shrink-0 flex items-center">
                {companyLogo ? (
                  <img 
                    src={companyLogo} 
                    alt={companyName}
                    className="w-40 h-auto object-contain"
                    style={{ maxHeight: '160px' }}
                    onError={(e) => {
                      console.error('Logo failed to load:', companyLogo);
                      const fileId = companyLogo.match(/id=([^&]+)/)?.[1];
                      if (fileId && !e.target.dataset.retried) {
                        e.target.dataset.retried = 'true';
                        e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                ) : (
                  <div className="w-40 h-40 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
                    LOGO
                  </div>
                )}
              </div>
              
              {/* Right - Services */}
              <div className="text-right text-xs">
                <p className="font-semibold">Freight Forwarder</p>
                <p className="font-semibold">Custom Broker</p>
                <p className="font-semibold">Reefer Transportation</p>
                <p className="font-semibold">Logistics Solution</p>
                {companyWebsite && (
                  <p className="mt-1 text-blue-600">{companyWebsite}</p>
                )}
              </div>
            </div>

            {/* Horizontal Line */}
            <div className="border-t-2 border-black mb-3"></div>

            {/* Title */}
            <div className="text-center mb-3">
              <h1 className="text-lg font-bold underline">CARGO ARRIVAL NOTICE</h1>
            </div>

            {/* Job No and Date Row */}
            <div className="flex justify-between mb-3 text-xs">
              <div>
                <span className="font-semibold">Job No: </span>
                <span>{formData.job_no || ''}</span>
              </div>
              <div>
                <span className="font-semibold">Date: </span>
                <span>{formatDate(formData.date)}</span>
              </div>
            </div>

            {/* Main Table */}
            <table className="w-full border-collapse border border-black text-xs mb-4">
              <tbody>
                {/* Row 1: Shipper, Consignee, Notify */}
                <tr>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Shipper</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.shipper || ''}</div>
                  </td>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Consignee</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.consignee || ''}</div>
                  </td>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Notify Party</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.notify || ''}</div>
                  </td>
                </tr>

                {/* Rows 2+: 2-column label | value layout (value spans 2 cols to match row 1's 3 cols) */}
                <tr>
                  <td className="border border-black p-2 w-1/3 font-semibold bg-gray-50">Flight Name</td>
                  <td className="border border-black p-2" colSpan="2">{formData.airline || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 w-1/3 font-semibold bg-gray-50">Flight No. / Date</td>
                  <td className="border border-black p-2" colSpan="2">
                    {formData.flight_no || ''}{formData.flight_date ? ` / ${formatDate(formData.flight_date)}` : ''}
                  </td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">Airport of Load</td>
                  <td className="border border-black p-2" colSpan="2">{formData.airport_of_load || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">Airport of Discharge</td>
                  <td className="border border-black p-2" colSpan="2">{formData.airport_of_discharge || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">MAWB No. / Date</td>
                  <td className="border border-black p-2" colSpan="2">
                    {formData.mawb_no || ''}{formData.mawb_date ? ` / ${formatDate(formData.mawb_date)}` : ''}
                  </td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">HAWB No. / Date</td>
                  <td className="border border-black p-2" colSpan="2">
                    {formData.hawb_no || ''}{formData.hawb_date ? ` / ${formatDate(formData.hawb_date)}` : ''}
                  </td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">No of Packages</td>
                  <td className="border border-black p-2" colSpan="2">{formData.no_of_packages || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">Gr. Wt.</td>
                  <td className="border border-black p-2" colSpan="2">
                    {formData.gross_weight || ''}{formData.gross_weight ? ' KG' : ''}
                  </td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50 align-top">Description</td>
                  <td className="border border-black p-2 min-h-[48px]" colSpan="2">{formData.description || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">IGM No.</td>
                  <td className="border border-black p-2" colSpan="2">{formData.igm_no || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">Date</td>
                  <td className="border border-black p-2" colSpan="2">{formatDate(formData.igm_date)}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold bg-gray-50">Temp</td>
                  <td className="border border-black p-2" colSpan="2">{formData.temperature || ''}</td>
                </tr>
              </tbody>
            </table>

            {/* Signature Area */}
            <div className="flex justify-end mb-4">
              <div className="text-right text-xs">
                <p className="font-semibold mb-8">For, {companyName}</p>
                <div className="border-t border-black pt-1 w-40">
                  <p className="text-center">Authorized Signatory</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div id="can-footer" className="mt-6">
              <div className="border-t-2 border-black mb-2"></div>
              <div className="text-center" style={{ fontSize: '9px', lineHeight: '1.6' }}>
                {/* Company name — bold */}
                <p style={{ fontWeight: 'bold', letterSpacing: '0.05em' }}>{companyName}</p>

                {/* Reg Off + Tel on same line */}
                <p>
                  {companyAddress && (
                    <>
                      <span style={{ fontWeight: 'bold' }}>Reg Off: </span>
                      <span>{companyAddress}</span>
                    </>
                  )}
                  {companyPhone && (
                    <>
                      <span style={{ fontWeight: 'bold' }}>{companyAddress ? '  Tel: ' : 'Tel: '}</span>
                      <span>{companyPhone}</span>
                    </>
                  )}
                </p>

                {/* GST + Email + LLP IN on same line */}
                {(companyGst || companyEmail || companyCin) && (
                  <p>
                    {companyGst && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>GST No.: </span>
                        <span>{companyGst}</span>
                      </>
                    )}
                    {companyEmail && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>{companyGst ? '  Email: ' : 'Email: '}</span>
                        <span>{companyEmail}</span>
                      </>
                    )}
                    {companyCin && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>{(companyGst || companyEmail) ? '  LLP IN No.: ' : 'LLP IN No.: '}</span>
                        <span>{companyCin}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles are injected by CANPrint.js parent */}
    </>
  );
});

CargoArrivalNoticePrint.displayName = 'CargoArrivalNoticePrint';

export default CargoArrivalNoticePrint;
